import router from 'next/router';
import { API, endpoints } from 'src/api';
import { useLoaderStore } from 'src/components/loaders';
import { formatFileResponse } from 'src/features/files';
import useSWR from 'swr';

export interface Owner {
  memberId: string;
  firstname: string;
  lastname: string;
  email: string;
  userType: string;
  avatar: string;
}
export interface WorkspaceType {
  workspaceId: string;
  name: string;
  isOpen?: boolean;
  isCustom?: boolean;
  members: any;
  owner?: Owner;
  icon?: any;
  access?: string;
  coverImage?: string;
  isPublic?: boolean;
  description?: string;
  collapseBoards: boolean;
  collapseDocuments: boolean;
  // isPopupOpen: boolean;
  workspaceMenuItems?: { label: string | JSX.Element; value: string; icon?: JSX.Element }[];
}

export const useWorkspaceService = () => {
  const useWorkspaces = ({ id }: { id?: string }) => {
    const { data, revalidate, mutate } = useSWR(endpoints.workspaces.userWorkspace.url, API.get);
    let workspaces = [];
    if (data?.data?.data?.length)
      workspaces = data.data.data.map((workspace) => formatWorkspace(workspace, id));
    return {
      userWorkspaces: workspaces as WorkspaceType[],
      revalidate,
      mutate,
    };
  };

  const formatWorkspace = (workspace, id): WorkspaceType => {
    return workspace.isOpen !== undefined
      ? {
          ...workspace,
        }
      : {
          ...workspace,
          isOpen:
            (router.query.workspaceId && router.query.workspaceId === workspace.workspaceId) ||
            (router.query.slug && workspace.workspaceId === id) ||
            (router.query.slug && workspace.workspaceId === id),
        };
  };

  async function fetchWorkspacebyId({ id }: { id: string }) {
    try {
      const response = await API.get(`/workspace?id=${id}`);
      if (response) {
        return response.data;
      }
    } catch (error) {
      if (error.response.data.data) {
        return error.response.data;
      }

      return null;
    }
    return null;
  }

  async function fetchWorkspaceFavorites({ id }: { id: string }) {
    try {
      const response = await API.get(`/files?page=1&limit=5&state=favorite&workspace=${id}`);
      const data = response.data.data.files;
      if (data) {
        const favoriteFiles = data.map((file) => formatFileResponse(file));
        return favoriteFiles;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  async function fetchWorkspaceFiles({ id }: { id: string }) {
    try {
      const response = await API.get(`/files?workspace=${id}&limit=4`);

      const data = response.data.data.files;
      if (response) {
        const workspaceFiles = data.map((file) => formatFileResponse(file));
        return workspaceFiles;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  async function onCreateNew({ id, value }) {
    try {
      const response =
        value === 'board'
          ? await API.post('/board', { name: '', workspace: id })
          : await API.post('/doc', {
              title: '',
              workspace: id,
            });
      if (response.data) {
        return response.data;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }
  async function onUpdateWorkspace({
    id,
    name,
    description,
    coverImage,
    icon,
    isCoverFit = false,
  }) {
    try {
      const payload = {
        workspaceId: id,
        name: name,
        description: description,
        coverImage: coverImage,
        icon: icon,
        isCoverFit: isCoverFit,
      };
      const response = await API.put(`/workspace?id=${id}`, payload);
      if (response) {
        return response;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  async function onInviteUsers({ id, members, emails }) {
    try {
      const response = API.post('/workspace/member', { id, members, emails });
      if (response) return response;
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  return {
    useWorkspaces,
    fetchWorkspacebyId,
    onUpdateWorkspace,
    fetchWorkspaceFavorites,
    onCreateNew,
    onInviteUsers,
    fetchWorkspaceFiles,
  };
};
